/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ContentVodOttActionDetailsGetRequest,
    OfferVodOttActionDetailsGetRequest,
    VodOttActionDetailsApi,
} from 'client-code/action-details';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IActionDetailsServiceRepository } from '../interfaces/IActionDetailsServiceRepository';
import { Log } from '../logging/Log';
import {
    TActionDetailsList,
    TActionDetailsParams,
    TActionDetailsParamsContentId,
    TServicesParams,
    TVodOttActionDetailsList,
} from '../types';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';

export class ActionDetailsServiceRepositoryImpl implements IActionDetailsServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'ActionDetailsServiceRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getCommonRequestParameters(commonHeaders) {
        return {
            acceptLanguage: commonHeaders['Accept-Language'],
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationName: commonHeaders.ApplicationName,
            applicationVersion: commonHeaders.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            deviceType: commonHeaders.DeviceType,
            productName: commonHeaders.ProductName,
            timeZoneName: this.mParams.timeZoneName,
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    private getConfigurationParameters(commonHeaders) {
        return {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
    }

    private async getContentActionDetailsFromService(
        vodOttActionDetailsParams: TActionDetailsParamsContentId,
    ): Promise<TActionDetailsList> {
        try {
            const {
                contentId,
                screenType,
                actionDetailsNetworkCallId,
                videoProviderId,
                excludeSoCu,
                whiny,
            } = vodOttActionDetailsParams;

            const commonHeaders = new CommonHeaders(screenType).headers;

            const requestParameters: ContentVodOttActionDetailsGetRequest = {
                contentId,
                ...this.getCommonRequestParameters(commonHeaders),
                ...(excludeSoCu && { excludeSoCu }),
                ...(videoProviderId && { videoProviderId }),
                ...(whiny && { whiny }),
            };

            const actionsApi = new VodOttActionDetailsApi(
                new Configuration(this.getConfigurationParameters(commonHeaders)),
            );

            const actionsApiResponseObj =
                await actionsApi.contentVodOttActionDetailsGet(requestParameters);

            const actionDetailsApiResponse: TActionDetailsList = {
                actionDetailsNetworkCallId,
                actionDetails: actionsApiResponseObj.data as TVodOttActionDetailsList,
            };
            return actionDetailsApiResponse;
        } catch (error: any) {
            error.actionDetailsNetworkCallId = vodOttActionDetailsParams.actionDetailsNetworkCallId;
            Log.e(this.TAG, 'getContentActionDetailsFromService()', error);
            throw error;
        }
    }

    private async getOfferActionDetailsFromService(
        vodOttActionDetailsParams: TActionDetailsParams,
    ): Promise<TActionDetailsList> {
        try {
            const { offerId, isCatchUp, isStartOver, screenType, actionDetailsNetworkCallId } =
                vodOttActionDetailsParams;
            const commonHeaders = new CommonHeaders(screenType).headers;

            const requestParameters: OfferVodOttActionDetailsGetRequest = {
                offerId,
                isCatchUp,
                isStartOver,
                ...this.getCommonRequestParameters(commonHeaders),
            };

            const configuration = new Configuration(this.getConfigurationParameters(commonHeaders));
            const actionsApi = new VodOttActionDetailsApi(configuration);
            const actionsApiResponseObj =
                await actionsApi.offerVodOttActionDetailsGet(requestParameters);
            const actionDetailsApiResponse: TActionDetailsList = {
                actionDetailsNetworkCallId,
                actionDetails: actionsApiResponseObj.data as TVodOttActionDetailsList,
            };
            return actionDetailsApiResponse;
        } catch (error: any) {
            error.actionDetailsNetworkCallId = vodOttActionDetailsParams.actionDetailsNetworkCallId;
            Log.e(this.TAG, 'getOfferActionDetailsFromService()', error);
            throw error;
        }
    }

    public async fetchContentActionDetails(
        actionDetailsParams: TActionDetailsParamsContentId,
    ): Promise<TActionDetailsList> {
        try {
            return await this.getContentActionDetailsFromService(actionDetailsParams);
        } catch (error) {
            Log.e(this.TAG, 'fetchContentActionDetails()', error);
            throw error;
        }
    }

    async fetchOfferActionDetails(
        actionDetailsParams: TActionDetailsParams,
    ): Promise<TActionDetailsList> {
        try {
            return await this.getOfferActionDetailsFromService(actionDetailsParams);
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }
}
